import { useRef, useMemo } from 'react'
import { useLoader, useFrame, useThree } from '@react-three/fiber'
import { EffectComposer, LUT } from '@react-three/postprocessing'
import { LUTCubeLoader } from 'postprocessing'
import * as THREE from 'three'
import { useScrollProgress } from '../hooks'

// Post-processing effects with LUT
export function PostEffects() {
  const texture = useLoader(LUTCubeLoader, '/assets/lut/F-6800-STD.cube')

  return (
    <EffectComposer>
      <LUT lut={texture} />
    </EffectComposer>
  )
}

// Static dark background - no animation for better performance
export function GradientBackground() {
  const { scene } = useThree()

  useMemo(() => {
    scene.background = new THREE.Color('#0a0a1a')
  }, [scene])

  return null
}
// Camera animation - optimized
export function CameraAnimation() {
  const { camera } = useThree()
  const scroll = useScrollProgress()

  // Pre-create vectors once to avoid GC pressure
  const refs = useRef({
    target: new THREE.Vector3(),
    currPos: new THREE.Vector3(),
    nextPos: new THREE.Vector3(),
    currTarget: new THREE.Vector3(),
    nextTarget: new THREE.Vector3()
  })

  const sections = useMemo(() => [
    { pos: [0.346, 3.864, 0.095], target: [0.337, -1, 0.222] },
    { pos: [2.337, 0.613, 0.306], target: [-0.034, 0.218, -0.009] },
    { pos: [-0.191, 5.064, -0.228], target: [-0.248, 0.026, 0.248] },
    { pos: [-2.688, 0.552, -0.077], target: [0, 0.134, 0.199] },
    { pos: [0.007, 4.256, -0.052], target: [0, 0, 0] },
    { pos: [0.007, 4.256, -0.052], target: [0, 0, 0] },
    { pos: [3.098, 2.648, -0.054], target: [-0.028, 0.096, 0.210] },
    { pos: [3.098, 2.648, -0.054], target: [-0.028, 0.096, 0.210] },
    { pos: [2.753, 1.256, 3.303], target: [-0.059, 0.010, 0.044] },
    { pos: [3.098, 2.648, -0.054], target: [-0.028, 0.096, 0.210] }
  ], [])

  useFrame(() => {
    const count = sections.length - 1
    const scaled = scroll * count
    const i = Math.floor(scaled)
    const next = Math.min(i + 1, count)
    const t = (scaled - i) ** 2 * (3 - 2 * (scaled - i))

    const curr = sections[i]
    const nextSec = sections[next]

    if (curr && nextSec) {
      // Reuse existing vectors instead of creating new ones
      refs.current.currPos.set(...curr.pos)
      refs.current.nextPos.set(...nextSec.pos)
      refs.current.currTarget.set(...curr.target)
      refs.current.nextTarget.set(...nextSec.target)

      camera.position.lerpVectors(refs.current.currPos, refs.current.nextPos, t)
      refs.current.target.lerpVectors(refs.current.currTarget, refs.current.nextTarget, t)
      camera.lookAt(refs.current.target)
    }
  })

  return null
}
